/**
 * ============================================================================
 * Main Entry Point - Supabase Edition
 * ============================================================================
 * 
 * This file initializes the React app with tRPC and Supabase authentication.
 * 
 * Status: ✅ SUPABASE-READY
 * 
 * ============================================================================
 */

// Import fetch interceptor FIRST to patch global fetch
import './lib/fetchInterceptor';
// Import i18n configuration
import './lib/i18n';

import { isConfigValid, getMissingConfigKeys } from '@/lib/runtimeConfig';
import { trpc, createTRPCClient } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

const queryClient = new QueryClient();

// Redirect to login if unauthorized
const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG || error.data?.code === 'UNAUTHORIZED';

  if (!isUnauthorized) return;

  // Redirect to login page
  window.location.href = '/login';
};

// Subscribe to query errors
queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

// Subscribe to mutation errors
queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

// Check configuration before starting the app
if (!isConfigValid()) {
  console.error('[App] Configuration is invalid or missing');
  const missingKeys = getMissingConfigKeys();
  console.error('[App] Missing keys:', missingKeys);
  
  // Dynamically import ConfigErrorPage to avoid bundling it in normal builds
  import('@/components/ConfigErrorPage').then(({ default: ConfigErrorPage }) => {
    createRoot(document.getElementById("root")!).render(
      <ConfigErrorPage missingKeys={missingKeys} />
    );
  });
} else {
  // Create tRPC client with Supabase authentication
  const trpcClient = createTRPCClient();

  createRoot(document.getElementById("root")!).render(
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  );
}

